"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useId } from "react";
import { useRuleWizard } from "./hooks";
import type { RuleWizardStep } from "./ruleWizard.type";

interface RuleWizardProps {
  // The numbered choice columns (goal, reward, how it's earned, who).
  steps: RuleWizardStep[];
  // Heading of the dark summary box, e.g. "Your programme".
  summaryTitle: string;
  // The WhatsApp-style bubble under the summary and its timestamp.
  message: string;
  messageTime: string;
  caption?: string;
}

// Interactive "build a rule" picker (the preview's `rule_wizard` visual):
// one single-choice column per step, and a live summary of the programme
// the choices describe.
const RuleWizard = ({
  steps,
  summaryTitle,
  message,
  messageTime,
  caption,
}: RuleWizardProps) => {
  const { selected, select, summary } = useRuleWizard(steps);
  const baseId = useId();

  return (
    <figure className="m-0">
      <div className="grid gap-[18px]">
        <div className="grid gap-3.5 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => {
            const headingId = `${baseId}-${step.key}`;
            return (
              <div
                key={step.key}
                className="grid content-start gap-2.5 rounded-[22px] bg-white p-[18px] shadow-card"
              >
                <span
                  aria-hidden="true"
                  className="grid size-[30px] place-items-center rounded-full bg-primary text-sm font-extrabold text-white"
                >
                  {index + 1}
                </span>
                <h3
                  id={headingId}
                  className="text-[15.5px] leading-snug font-bold"
                >
                  <span className="sr-only">{`${index + 1}. `}</span>
                  {step.title}
                </h3>
                <div
                  role="group"
                  aria-labelledby={headingId}
                  className="grid gap-2"
                >
                  {step.options.map((option) => {
                    const isOn = selected[step.key] === option;
                    return (
                      <Button
                        key={option}
                        type="button"
                        variant="white"
                        aria-pressed={isOn}
                        onClick={() => select(step.key, option)}
                        className={cn(
                          "h-auto justify-start rounded-xl border-[1.5px] px-3 py-2.5 text-left text-[14.5px] leading-snug font-semibold whitespace-normal transition-colors duration-200 motion-reduce:transition-none",
                          isOn
                            ? "border-primary bg-tone-blush text-primary hover:bg-tone-blush"
                            : "border-line bg-white text-ink hover:border-line-warm hover:bg-white",
                        )}
                      >
                        {option}
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid gap-2.5 rounded-[22px] bg-ink px-6 py-5 text-white">
          <p className="text-base font-bold">{summaryTitle}</p>
          <p aria-live="polite" className="text-[16.5px] leading-relaxed">
            {summary.map((part) => (
              <span key={part.label}>
                <b className="text-line-warm">{part.label}:</b> {part.value}
                .{" "}
              </span>
            ))}
          </p>
          <p className="relative max-w-[420px] justify-self-start rounded-xl rounded-tl-[2px] bg-white px-2.5 pt-2 pb-[18px] text-[13px] leading-[1.4] text-ink shadow-xs">
            {message}
            <time className="absolute right-2 bottom-1 text-[10px] text-ink-muted">
              {messageTime}
            </time>
          </p>
        </div>
      </div>
      {caption && (
        <figcaption className="px-1 pt-3 text-[12.5px] text-ink-muted">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

export default RuleWizard;
